import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Lock } from "lucide-react";
import {
  type EventType,
  type LocationType,
  type QuestionFieldType,
  type EventTypeQuestionInput,
  createEventType,
  updateEventType,
  getEventTypeQuestions,
  replaceEventTypeQuestions,
} from "@/lib/bookingApi";

// ── Helpers ───────────────────────────────────────────────────────────────────

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

interface QuestionDraft {
  label: string;
  field_type: QuestionFieldType;
  required: boolean;
  optionsText: string; // comma-separated, only meaningful for "select"
}

interface FormState {
  name: string;
  slug: string;
  slugTouched: boolean;
  description: string;
  duration_min: string;
  buffer_before_min: string;
  buffer_after_min: string;
  min_notice_hours: string; // UI in hours; stored as minutes
  max_advance_days: string;
  daily_cap: string; // empty = unlimited
  location_type: LocationType;
  location_detail: string;
  is_active: boolean;
}

const emptyForm = (): FormState => ({
  name: "",
  slug: "",
  slugTouched: false,
  description: "",
  duration_min: "30",
  buffer_before_min: "0",
  buffer_after_min: "0",
  min_notice_hours: "24",
  max_advance_days: "60",
  daily_cap: "",
  location_type: "zoom",
  location_detail: "",
  is_active: true,
});

const formFromEventType = (et: EventType): FormState => ({
  name: et.name,
  slug: et.slug,
  slugTouched: true,
  description: et.description ?? "",
  duration_min: String(et.duration_min),
  buffer_before_min: String(et.buffer_before_min),
  buffer_after_min: String(et.buffer_after_min),
  min_notice_hours: String(Math.round((et.min_notice_min / 60) * 100) / 100),
  max_advance_days: String(et.max_advance_days),
  daily_cap: et.daily_cap == null ? "" : String(et.daily_cap),
  location_type: et.location_type,
  location_detail: et.location_detail ?? "",
  is_active: et.is_active,
});

interface EventTypeEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create a new event type */
  eventType: EventType | null;
}

export function EventTypeEditor({ open, onOpenChange, eventType }: EventTypeEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm());
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [saving, setSaving] = useState(false);

  // Existing questions (edit mode only).
  const questionsQuery = useQuery({
    queryKey: ["booking-event-type-questions", eventType?.id],
    queryFn: () => getEventTypeQuestions(eventType!.id),
    enabled: open && !!eventType,
  });

  // Re-seed form state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setForm(eventType ? formFromEventType(eventType) : emptyForm());
    setQuestions([]);
  }, [open, eventType]);

  useEffect(() => {
    if (open && eventType && questionsQuery.data) {
      setQuestions(
        questionsQuery.data.questions.map((q) => ({
          label: q.label,
          field_type: q.field_type,
          required: q.required,
          optionsText: (q.options ?? []).join(", "),
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventType?.id, questionsQuery.data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onNameChange = (name: string) =>
    setForm((f) => ({ ...f, name, slug: f.slugTouched ? f.slug : slugify(name) }));

  // ── Question list editing ───────────────────────────────────────────────────

  const addQuestion = () =>
    setQuestions((qs) => [...qs, { label: "", field_type: "text", required: false, optionsText: "" }]);

  const updateQuestion = (i: number, patch: Partial<QuestionDraft>) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const removeQuestion = (i: number) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  const moveQuestion = (i: number, dir: -1 | 1) =>
    setQuestions((qs) => {
      const j = i + dir;
      if (j < 0 || j >= qs.length) return qs;
      const next = [...qs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  // ── Save ────────────────────────────────────────────────────────────────────

  const validate = (): string | null => {
    if (!form.name.trim()) return "Name is required";
    if (!form.slug.trim()) return "URL slug is required";
    const duration = Number(form.duration_min);
    if (!Number.isInteger(duration) || duration < 5 || duration > 480)
      return "Duration must be between 5 and 480 minutes";
    if ((form.location_type === "phone" || form.location_type === "in_person") && !form.location_detail.trim())
      return form.location_type === "phone"
        ? "Phone instructions are required for phone meetings"
        : "Address is required for in-person meetings";
    for (const q of questions) {
      if (!q.label.trim()) return "Every question needs a label";
      if (q.field_type === "select") {
        const opts = q.optionsText.split(",").map((s) => s.trim()).filter(Boolean);
        if (opts.length < 2) return `Dropdown question "${q.label}" needs at least 2 comma-separated options`;
      }
    }
    return null;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const num = (s: string, fallback: number) => {
        const n = Number(s);
        return Number.isFinite(n) ? Math.round(n) : fallback;
      };
      const payload = {
        name: form.name.trim(),
        slug: slugify(form.slug),
        description: form.description.trim() || null,
        duration_min: num(form.duration_min, 30),
        buffer_before_min: num(form.buffer_before_min, 0),
        buffer_after_min: num(form.buffer_after_min, 0),
        min_notice_min: Math.max(0, Math.round(Number(form.min_notice_hours || "0") * 60)),
        max_advance_days: num(form.max_advance_days, 60),
        daily_cap: form.daily_cap.trim() === "" ? null : num(form.daily_cap, 0),
        location_type: form.location_type,
        location_detail:
          form.location_type === "zoom" ? null : form.location_detail.trim() || null,
        is_active: form.is_active,
      };

      let savedId: string;
      if (eventType) {
        const result = await updateEventType(eventType.id, payload);
        savedId = result.eventType?.id ?? eventType.id;
      } else {
        const result = await createEventType(payload);
        if (!result.eventType) throw new Error("Failed to create event type");
        savedId = result.eventType.id;
      }

      const questionInputs: EventTypeQuestionInput[] = questions.map((q, i) => ({
        label: q.label.trim(),
        field_type: q.field_type,
        required: q.required,
        ...(q.field_type === "select"
          ? { options: q.optionsText.split(",").map((s) => s.trim()).filter(Boolean) }
          : {}),
        list_index: i,
      }));
      await replaceEventTypeQuestions(savedId, questionInputs);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-my-page"] });
      queryClient.invalidateQueries({ queryKey: ["booking-event-type-questions"] });
      toast({ title: eventType ? "Event type updated" : "Event type created" });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
    onSettled: () => setSaving(false),
  });

  const handleSave = () => {
    const error = validate();
    if (error) {
      toast({ title: "Check the form", description: error, variant: "destructive" });
      return;
    }
    setSaving(true);
    saveMutation.mutate();
  };

  const locationDetailLabel =
    form.location_type === "phone" ? "Phone instructions" : "Meeting address";
  const locationDetailPlaceholder =
    form.location_type === "phone"
      ? "e.g. We will call you at the number you provide"
      : "e.g. 123 Main St, Suite 200, Fredericksburg, VA";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{eventType ? "Edit Event Type" : "New Event Type"}</DialogTitle>
          <DialogDescription>
            Define a meeting type invitees can book on your page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="et-name">Name</Label>
              <Input
                id="et-name"
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g. Tax Planning Call"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-slug">URL slug</Label>
              <Input
                id="et-slug"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value, slugTouched: true }))}
                placeholder="tax-planning-call"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="et-desc">Description</Label>
            <Textarea
              id="et-desc"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Shown to invitees when picking a meeting type (optional)"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="et-duration">Duration (min)</Label>
              <Input
                id="et-duration"
                type="number"
                min={5}
                max={480}
                value={form.duration_min}
                onChange={(e) => set("duration_min", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-buf-before">Buffer before (min)</Label>
              <Input
                id="et-buf-before"
                type="number"
                min={0}
                value={form.buffer_before_min}
                onChange={(e) => set("buffer_before_min", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-buf-after">Buffer after (min)</Label>
              <Input
                id="et-buf-after"
                type="number"
                min={0}
                value={form.buffer_after_min}
                onChange={(e) => set("buffer_after_min", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-notice">Min notice (hours)</Label>
              <Input
                id="et-notice"
                type="number"
                min={0}
                step="0.5"
                value={form.min_notice_hours}
                onChange={(e) => set("min_notice_hours", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-advance">Max advance (days)</Label>
              <Input
                id="et-advance"
                type="number"
                min={1}
                value={form.max_advance_days}
                onChange={(e) => set("max_advance_days", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-cap">Daily cap</Label>
              <Input
                id="et-cap"
                type="number"
                min={1}
                value={form.daily_cap}
                onChange={(e) => set("daily_cap", e.target.value)}
                placeholder="Unlimited"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Select
                value={form.location_type}
                onValueChange={(v) => set("location_type", v as LocationType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom meeting</SelectItem>
                  <SelectItem value="phone">Phone call</SelectItem>
                  <SelectItem value="in_person">In person</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.location_type !== "zoom" && (
              <div className="space-y-1.5">
                <Label htmlFor="et-loc-detail">{locationDetailLabel}</Label>
                <Input
                  id="et-loc-detail"
                  value={form.location_detail}
                  onChange={(e) => set("location_detail", e.target.value)}
                  placeholder={locationDetailPlaceholder}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Switch id="et-active" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} />
            <Label htmlFor="et-active" className="cursor-pointer">
              Active — bookable on your public page
            </Label>
          </div>

          {/* ── Intake questions ─────────────────────────────────────────────── */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Intake questions</h3>
              <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add question
              </Button>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              Name and email are always collected — no need to add them here.
            </div>

            {eventType && questionsQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading questions…
              </div>
            )}

            {questions.map((q, i) => (
              <div key={i} className="border rounded-md p-3 space-y-2 bg-muted/20">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <Input
                      value={q.label}
                      onChange={(e) => updateQuestion(i, { label: e.target.value })}
                      placeholder="Question label (e.g. What would you like to discuss?)"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                      <Select
                        value={q.field_type}
                        onValueChange={(v) =>
                          updateQuestion(i, { field_type: v as QuestionFieldType })
                        }
                      >
                        <SelectTrigger className="h-8 w-36 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Short text</SelectItem>
                          <SelectItem value="textarea">Long text</SelectItem>
                          <SelectItem value="phone">Phone number</SelectItem>
                          <SelectItem value="select">Dropdown</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox
                          checked={q.required}
                          onCheckedChange={(v) => updateQuestion(i, { required: v === true })}
                        />
                        Required
                      </label>
                    </div>
                    {q.field_type === "select" && (
                      <Input
                        value={q.optionsText}
                        onChange={(e) => updateQuestion(i, { optionsText: e.target.value })}
                        placeholder="Options, comma-separated (e.g. Individual, Business, Both)"
                        className="text-sm"
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={i === 0}
                      onClick={() => moveQuestion(i, -1)}
                      title="Move up"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={i === questions.length - 1}
                      onClick={() => moveQuestion(i, 1)}
                      title="Move down"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removeQuestion(i)}
                      title="Remove question"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {questions.length === 0 && !questionsQuery.isLoading && (
              <p className="text-sm text-muted-foreground">
                No extra questions — invitees will only be asked for their name and email.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {eventType ? "Save changes" : "Create event type"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
